import { StorageProvider } from "@quotientjs/browser-storage"

import { DeviceIdStore, DeviceId } from "@/stores/device-id"
import { SessionIdStore, SessionId } from "@/stores/session-id"
import { PersonIdStore, PersonId } from "@/stores/person-id"
import {
  BrowserFingerprintStore,
  BrowserFingerprint,
} from "@/stores/browser-fingerprint"

/**
 * Notified after the store changes what it holds. `BrowserContextManager` uses
 * this to keep the `qt_browser_context` cookie in step with browser storage.
 */
export type StoreListener = {
  personIdChanged(): void
  cleared(): void
}

/**
 * Gives SDK clients a shared view of IDs stored in the browser. If the browser
 * rejects a write, the latest person ID remains available in memory.
 */
export class Store {
  private readonly provider: StorageProvider
  private readonly listener: StoreListener | undefined
  private personIdValue: PersonId | null
  // After a failed write, remember the old stored value so it cannot replace
  // the newer in-memory value.
  private staleStoredPersonId: PersonId | null | undefined

  constructor(provider: StorageProvider, listener?: StoreListener) {
    this.provider = provider
    this.listener = listener
    this.personIdValue = PersonIdStore.get(provider)
    this.staleStoredPersonId = undefined
  }

  /** Returns the device ID, or null if none has been set. */
  deviceId(): DeviceId | null {
    return DeviceIdStore.get(this.provider)
  }

  /** Returns the session ID, or null if none has been set. */
  sessionId(): SessionId | null {
    return SessionIdStore.get(this.provider)
  }

  /** Returns the browser fingerprint, or null if none has been set. */
  browserFingerprint(): BrowserFingerprint | null {
    return BrowserFingerprintStore.get(this.provider)
  }

  /**
   * Returns the current person ID, including a newer value that could not yet
   * be saved to browser storage.
   */
  personId(): PersonId | null {
    const stored = PersonIdStore.get(this.provider)

    if (this.staleStoredPersonId === undefined) {
      this.personIdValue = stored
    } else if (stored === this.personIdValue) {
      this.staleStoredPersonId = undefined
    } else if (stored !== this.staleStoredPersonId) {
      // A different stored value was written later, usually by another tab.
      this.personIdValue = stored
      this.staleStoredPersonId = undefined
    } else {
      this.persistPersonId()
    }

    return this.personIdValue
  }

  /** Saves the person ID and uses it for later analytics events. */
  setPersonId(personId: PersonId): void {
    this.personIdValue = personId
    this.persistPersonId()
    this.listener?.personIdChanged()
  }

  /** Clears all SDK data from browser storage and memory. */
  clear(): void {
    this.personIdValue = null
    this.provider.clear("local")
    this.provider.clear("session")
    this.provider.clear("cookie")
    this.recordPersistenceResult()
    this.listener?.cleared()
  }

  private persistPersonId(): void {
    if (this.personIdValue === null) {
      PersonIdStore.remove(this.provider)
    } else {
      PersonIdStore.set(this.provider, this.personIdValue)
    }

    this.recordPersistenceResult()
  }

  private recordPersistenceResult(): void {
    const stored = PersonIdStore.get(this.provider)
    this.staleStoredPersonId =
      stored === this.personIdValue ? undefined : stored
  }
}
